<li
    {id}
    role="treeitem"
    on:click={toggleExpanded}
    on:keydown={keyHandler}
    tabindex={selected ? 0 : -1}
    class="prom-lo-tree-item"
    aria-expanded={expanded}
    aria-busy={learningObjective.childrenLoading && !children.length}
>
    <div
        class="prom-lo-tree-item__title {cssClass}"
        class:prom-lo-tree-item__title--root={isRoot}
        class:prom-lo-tree-item__title--selected={selected}
        class:prom-lo-tree-item__title--is-folder={learningObjective.isFolder}
        class:prom-lo-tree-item__title--is-loading={learningObjective.childrenLoading && !children.length}
        class:prom-lo-tree-item__title--no-items={learningObjective.childrenLoaded && !children.length}
        class:prom-lo-tree-item__title--expanded={expanded}
    >
        <div class="prom-lo-tree-item__icon"></div>
        <span>{learningObjective.title}</span>
    </div>
    {#if expanded}
        <ul role="group" class="prom-lo-tree-item__children" in:slide>
            {#each children as lo (lo.id)}
                <svelte:self
                    learningObjective={lo}
                    {selectedLearningObjective}
                    {learningObjectives}
                    on:select
                />
            {/each}
        </ul>
    {/if}
</li>

<script>
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { slide } from 'svelte/transition';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    /**
     * @typedef LearningObjective
     * @type {object}
     * @property {number} id
     * @property {number} parentId
     * @property {string} title
     * @property {string} cssClass
     * @property {boolean} isFolder
     * @property {boolean} [childrenLoading]
     * @property {boolean} [childrenLoaded]
     */

    /** @type { LearningObjective }*/
    export let learningObjective;
    /** @type { LearningObjective }*/
    export let selectedLearningObjective = null;
    /** @type { LearningObjective[] }*/
    export let learningObjectives = [];

    let expanded = learningObjective.id === 0;

    $: id = `lo-tree-item-${learningObjective.id}`;
    $: selected = selectedLearningObjective && selectedLearningObjective.id === learningObjective.id;
    $: isRoot = learningObjective.id === 0;
    $: children = learningObjectives.filter(lo => lo.parentId === learningObjective.id && lo.isFolder);
    $: cssClass = learningObjective.cssClass ? 'prom-lo-tree-item__title--' + learningObjective.cssClass.toLowerCase() : '';

    $: if (selectedLearningObjective && selectedLearningObjective.parentId === learningObjective.id) {
        expanded = true;
    }

    function toggleExpanded(e) {
        e.preventDefault();
        e.stopPropagation();

        if (!isRoot && learningObjective.isFolder) {
            expanded = !expanded;
        }

        dispatch('select', learningObjective);
    }

    function keyHandler(event) {
        const { key } = event;

        switch (normalizeKey(key)) {
            case Keys.SPACE:
            case Keys.ENTER:
                toggleExpanded(event);
                break;
            case Keys.RIGHT:
                onRight(event);
                break;
            case Keys.LEFT:
                onLeft(event);
                break;
            case Keys.DOWN:
                selectRelative(event, 1);
                break;
            case Keys.UP:
                selectRelative(event, -1);
                break;
            case Keys.TAB:
                onTab(event);
                
                break;
        }
    }

    function onTab(event) {
        if (event.shiftKey) {
            const items = [...document.querySelector('.prom-lo-tree').querySelectorAll('.prom-lo-tree-item')];
            const root = items[0];

            if (root.id !== id) {
                event.preventDefault();
                event.stopPropagation();

                // @ts-ignore .focus for the element
                root.focus();
            }
        }
    }

    function onLeft(event) {
        if (expanded) {
            toggleExpanded(event);
        } else {
            const items = [...document.querySelector('.prom-lo-tree').querySelectorAll('.prom-lo-tree-item')];
            const parent = items.find(i => i.id === `lo-tree-item-${learningObjective.parentId}`);

            if (parent) {
                // @ts-ignore .focus for the element
                parent.focus();
            }
        }
    }

    function onRight(event) {
        if (expanded) {
            selectRelative(event, 1);
        } else {
            toggleExpanded(event);
        }
    }

    function selectRelative(event, offset) {
        event.preventDefault();
        event.stopPropagation();

        const items = [...document.querySelector('.prom-lo-tree').querySelectorAll('.prom-lo-tree-item')];
        const itemIndex = items.findIndex(i => i.id === id);

        if (itemIndex + offset < items.length && itemIndex + offset >= 0) {
            // @ts-ignore .focus for the element
            items[itemIndex + offset].focus();
        }
    }
</script>
